/** @jsx jsx */

import React, { useEffect, useMemo } from "react";
import { jsx } from "@emotion/core";
import { Switch, Route, useParams } from "react-router-dom";

//components
import CompanyDetailsFold from "./companyDetailsFold";
import ApplicantDetailsFold from "./applicantDetailsFold";
import ReceivablesDetailsFold from "./receivablesDetailsFold";
import EligibilityAmountFold from "./eligibilityAmountFold";
import FoldNavigation from "./FoldNavigation";
import EligibilityViewSteps from "./EligibilityViewSteps";
import AccountComponent from "../currentBankAccountDetails/CurrentBankAccountDetails";
import KYC from "../KYC/KYC";

import { EligibilityRoutes, routes } from "../../Routes";
import useEligibilityDetails from "../../hooks/eligibility/useEligibilityDetails";
import useEligibilityRouting from "../../hooks/eligibility/useEligibilityRouting";
import useEligibilityMetaData from "../../hooks/auth/useEligibilityMetaData";

const EligibilityView: React.FC = () => {
  //NOTE: TypeSafe way to read fold info:
  const { fold } = useParams<{ fold: EligibilityRoutes }>();
  // foldIndex will be -1 for 'documents' and 'bank-account-details'.
  const foldIndex = useMemo(
    () =>
      Object.values(EligibilityRoutes).findIndex(
        value => value.toString() === fold
      ),
    [fold]
  );

  const {
    eligibilityState: { eligibilityDetails, submitting },
    saveEligibilityDetails,
    getEligibilityDetails
  } = useEligibilityDetails();

  const { completedTillIndex: completedTill } = useEligibilityRouting();

  //fetching eligibilityDetails
  useEffect(() => {
    getEligibilityDetails({});
  }, [getEligibilityDetails]);

  const {
    eligibilityMetaData: { loading: loadingMetaData }
  } = useEligibilityMetaData();

  // Rendering Bank Account details and Documents page.
  if (foldIndex === -1) {
    return (
      <div className="mx-0 mt-2 mx-lg-2 mt-lg-4 mb-4">
        <EligibilityViewSteps fold={fold} foldIndex={foldIndex} />
        <Switch>
          <Route path={routes.eligibility.kyc}>
            <KYC />
          </Route>
          <Route path={routes.eligibility.bankAccountDetails}>
            {eligibilityDetails && <AccountComponent />}
          </Route>
        </Switch>
      </div>
    );
  }

  if (!eligibilityDetails || completedTill === null || loadingMetaData) {
    return null;
  }

  return (
    <div className="mx-0 mt-2 mx-lg-2 mt-lg-4 mb-4">
      <EligibilityViewSteps fold={fold} foldIndex={foldIndex} />
      <FoldNavigation
        //converting to index
        completedTill={completedTill - 1}
        currentIndex={foldIndex}
      />
      <Switch>
        <Route path={routes.eligibility.view(EligibilityRoutes.CompanyFold)}>
          <CompanyDetailsFold
            eligibilityDetails={eligibilityDetails}
            submitting={submitting}
            saveEligibilityDetails={saveEligibilityDetails}
          />
        </Route>
        <Route path={routes.eligibility.view(EligibilityRoutes.ApplicantFold)}>
          <ApplicantDetailsFold
            eligibilityDetails={eligibilityDetails}
            submitting={submitting}
            saveEligibilityDetails={saveEligibilityDetails}
          />
        </Route>

        <Route
          path={routes.eligibility.view(EligibilityRoutes.ReceivablesFold)}
        >
          <ReceivablesDetailsFold
            eligibilityDetails={eligibilityDetails}
            submitting={submitting}
            saveEligibilityDetails={saveEligibilityDetails}
          />
        </Route>
        <Route
          path={routes.eligibility.view(
            EligibilityRoutes.EligibilityAmountFold
          )}
        >
          <EligibilityAmountFold
            eligibilityDetails={eligibilityDetails}
            submitting={submitting}
          />
        </Route>
      </Switch>
    </div>
  );
};

export default EligibilityView;
